// Email templates for Automation (GoHighLevel-style): a design is a theme plus a list of blocks, rendered to
// email-safe HTML (tables + inline styles, 600px). Pure — the builder previews with it and the engine sends
// with it, so what you see is what is sent. Starters follow remotetalent.com.au's look.
export const RT_THEME = { brand: '#004AAD', accent: '#FFBD59', background: '#F3F6FB', text: '#3F4752', heading: '#1F2938' };
export const RT_LOGO = 'https://remotetalent.com.au/wp-content/uploads/2025/03/Remote-talent.png';
export const RT_SITE = 'https://remotetalent.com.au/';
const RT_FOOTER = 'Remote Talent · 123 Pitt Street, Sydney NSW 2000 · 1300 009 347\nThe Employee You Need Without The Overhead';
export const EMAIL_BLOCK_TYPES = [
    { type: 'logo', label: 'Logo' },
    { type: 'heading', label: 'Heading' },
    { type: 'text', label: 'Text' },
    { type: 'button', label: 'Button' },
    { type: 'image', label: 'Image' },
    { type: 'divider', label: 'Divider' },
    { type: 'spacer', label: 'Spacer' },
    { type: 'footer', label: 'Footer' },
];
/** A new block of a type, with sensible starting content. */
export function newEmailBlock(type, id) {
    switch (type) {
        case 'logo':
            return { id, type, src: RT_LOGO, url: RT_SITE, size: 200, align: 'center' };
        case 'heading':
            return { id, type, text: 'Your heading', size: 26, align: 'left' };
        case 'text':
            return { id, type, text: 'Write your message here.', align: 'left' };
        case 'button':
            return { id, type, text: 'Book Your Free Consultation', url: RT_SITE, style: 'accent', align: 'center' };
        case 'image':
            return { id, type, src: '', size: 520, align: 'center' };
        case 'divider':
            return { id, type };
        case 'spacer':
            return { id, type, size: 24 };
        case 'footer':
            return { id, type, text: RT_FOOTER, align: 'center' };
    }
}
/** The fields a template may use, for the builder's help line. */
export const EMAIL_VARS = [
    { tag: '{{contact.first_name}}', label: 'First name' },
    { tag: '{{contact.name}}', label: 'Full name' },
    { tag: '{{contact.email}}', label: 'Email' },
    { tag: '{{contact.phone}}', label: 'Phone' },
];
/** Replace {{contact.*}} with the contact's details (empty when unknown; "there" for a missing first name), and
 *  {{lead.*}} / {{form.*}} / {{webhook.*}} with the trigger's own data (empty when the trigger didn't have it). */
export function fillEmailVars(text, vars) {
    const name = (vars.name ?? '').trim();
    const first = name.split(/\s+/)[0] ?? '';
    return text
        .replace(/\{\{\s*contact\.(first_name|name|email|phone)\s*\}\}/g, (_, k) => {
        if (k === 'first_name')
            return first || 'there';
        if (k === 'name')
            return name;
        if (k === 'email')
            return (vars.email ?? '').trim();
        return (vars.phone ?? '').trim();
    })
        .replace(/\{\{\s*(lead|form|facebook|webhook|application)\.([a-z0-9_]+)\s*\}\}/gi, (_, ns, key) => (vars.values ?? {})[`${ns.toLowerCase()}.${key.toLowerCase()}`] ?? '');
}
// ── Rendering ────────────────────────────────────────────────────────────────────────────────
const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const safeUrl = (u) => {
    const v = (u ?? '').trim();
    return /^(https?:\/\/|mailto:|tel:)/i.test(v) ? v : '';
};
const COLOR = /^#[0-9a-f]{3}([0-9a-f]{3})?$/i;
const color = (c, fallback) => (c && COLOR.test(c) ? c : fallback);
const num = (n, min, max, fallback) => (typeof n === 'number' && Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : fallback);
const HEADING_FONT = "Montserrat,'Segoe UI',Arial,Helvetica,sans-serif";
const BODY_FONT = "'Open Sans','Segoe UI',Arial,Helvetica,sans-serif";
function blockHtml(b, t, vars) {
    const align = b.align === 'center' ? 'center' : 'left';
    const words = (s) => esc(fillEmailVars(s ?? '', vars)).replace(/\r?\n/g, '<br>');
    const cell = (inner, pad = '0 32px') => `<tr><td style="padding:${pad};text-align:${align}">${inner}</td></tr>`;
    switch (b.type) {
        case 'logo': {
            const src = safeUrl(b.src);
            if (!src)
                return '';
            const img = `<img src="${esc(src)}" alt="Remote Talent" width="${num(b.size, 60, 520, 200)}" style="display:inline-block;border:0;max-width:100%;height:auto">`;
            const href = safeUrl(b.url);
            return cell(href ? `<a href="${esc(href)}">${img}</a>` : img, '28px 32px 12px');
        }
        case 'heading':
            return cell(`<h1 style="margin:0;font-family:${HEADING_FONT};font-size:${num(b.size, 16, 44, 26)}px;line-height:1.25;font-weight:700;color:${t.heading}">${words(b.text)}</h1>`, '12px 32px 8px');
        case 'text':
            return cell(`<p style="margin:0;font-family:${BODY_FONT};font-size:15px;line-height:1.65;color:${t.text}">${words(b.text)}</p>`, '8px 32px');
        case 'button': {
            const href = safeUrl(b.url) || '#';
            const bg = b.style === 'brand' ? t.brand : t.accent;
            const fg = b.style === 'brand' ? '#FFFFFF' : '#111111';
            return cell(`<a href="${esc(href)}" style="display:inline-block;padding:14px 28px;border-radius:8px;background:${bg};color:${fg};font-family:${BODY_FONT};font-size:15px;font-weight:600;text-decoration:none">${words(b.text)}</a>`, '16px 32px');
        }
        case 'image': {
            const src = safeUrl(b.src);
            if (!src)
                return '';
            return cell(`<img src="${esc(src)}" alt="" width="${num(b.size, 60, 536, 520)}" style="display:inline-block;border:0;max-width:100%;height:auto;border-radius:8px">`, '12px 32px');
        }
        case 'divider':
            return `<tr><td style="padding:16px 32px"><div style="border-top:1px solid #E5E9F0;line-height:0;font-size:0">&nbsp;</div></td></tr>`;
        case 'spacer':
            return `<tr><td style="height:${num(b.size, 4, 120, 24)}px;line-height:0;font-size:0">&nbsp;</td></tr>`;
        case 'footer':
            return `<tr><td style="padding:24px 32px 28px;text-align:${align};background:#F8FAFC;border-top:1px solid #E5E9F0;font-family:${BODY_FONT};font-size:12px;line-height:1.6;color:#8A94A6">${words(b.text)}</td></tr>`;
    }
}
/** A design as a complete, email-safe HTML document, with the contact's details filled in. */
export function renderEmailHtml(design, vars = {}) {
    const t = {
        brand: color(design.theme?.brand, RT_THEME.brand),
        accent: color(design.theme?.accent, RT_THEME.accent),
        background: color(design.theme?.background, RT_THEME.background),
        text: color(design.theme?.text, RT_THEME.text),
        heading: color(design.theme?.heading, RT_THEME.heading),
    };
    const rows = (design.blocks ?? []).map((b) => blockHtml(b, t, vars)).join('');
    return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title></title></head><body style="margin:0;padding:0;background:${t.background}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${t.background}"><tr><td align="center" style="padding:28px 12px"><table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:100%;max-width:600px;background:#FFFFFF;border-radius:14px;overflow:hidden;border-top:5px solid ${t.brand}">${rows}<tr><td style="height:8px;line-height:0;font-size:0">&nbsp;</td></tr></table></td></tr></table></body></html>`;
}
/** The plain-text version sent alongside the HTML (headings, text, buttons with their links, footer). */
export function renderEmailText(design, vars = {}) {
    const out = [];
    for (const b of design.blocks ?? []) {
        const w = fillEmailVars(b.text ?? '', vars).trim();
        if ((b.type === 'heading' || b.type === 'text' || b.type === 'footer') && w)
            out.push(w);
        if (b.type === 'button' && w)
            out.push(`${w}: ${safeUrl(b.url) || ''}`.trim());
    }
    return out.join('\n\n');
}
// ── Check ────────────────────────────────────────────────────────────────────────────────────
export const EMAIL_TEMPLATE_BLOCKS_MAX = 40;
/** Check a template before it is saved: a name, known blocks, sane sizes, safe links. */
export function validateEmailTemplate(raw) {
    const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    const name = typeof src.name === 'string' ? src.name.trim() : '';
    if (!name)
        return { ok: false, error: 'Give the template a name' };
    if (name.length > 120)
        return { ok: false, error: 'The name is too long (120 characters at most)' };
    const subject = typeof src.subject === 'string' ? src.subject.trim().slice(0, 200) : '';
    const d = src.design && typeof src.design === 'object' ? src.design : {};
    const th = d.theme && typeof d.theme === 'object' ? d.theme : {};
    const theme = {
        brand: color(th.brand, RT_THEME.brand),
        accent: color(th.accent, RT_THEME.accent),
        background: color(th.background, RT_THEME.background),
        text: color(th.text, RT_THEME.text),
        heading: color(th.heading, RT_THEME.heading),
    };
    const rawBlocks = Array.isArray(d.blocks) ? d.blocks : [];
    if (rawBlocks.length > EMAIL_TEMPLATE_BLOCKS_MAX)
        return { ok: false, error: `${EMAIL_TEMPLATE_BLOCKS_MAX} blocks at most` };
    const types = new Set(EMAIL_BLOCK_TYPES.map((b) => b.type));
    const blocks = [];
    for (let i = 0; i < rawBlocks.length; i++) {
        const b = rawBlocks[i];
        if (!b || typeof b !== 'object' || !types.has(b.type))
            return { ok: false, error: `Block ${i + 1}: unknown block` };
        const text = typeof b.text === 'string' ? b.text.slice(0, 5000) : undefined;
        for (const k of ['url', 'src']) {
            const v = typeof b[k] === 'string' ? b[k].trim() : '';
            if (v && !safeUrl(v))
                return { ok: false, error: `Block ${i + 1}: links and pictures must start with https://` };
        }
        blocks.push({
            id: typeof b.id === 'string' && b.id ? b.id.slice(0, 40) : `b${i + 1}`,
            type: b.type,
            ...(text !== undefined ? { text } : {}),
            ...(typeof b.url === 'string' && b.url.trim() ? { url: b.url.trim() } : {}),
            ...(typeof b.src === 'string' && b.src.trim() ? { src: b.src.trim() } : {}),
            ...(b.align === 'center' || b.align === 'left' ? { align: b.align } : {}),
            ...(typeof b.size === 'number' && Number.isFinite(b.size) ? { size: Math.round(b.size) } : {}),
            ...(b.style === 'brand' || b.style === 'accent' ? { style: b.style } : {}),
        });
    }
    return { ok: true, value: { name, subject, design: { theme, blocks } } };
}
const block = (id, b) => ({ id, ...b });
const logo = block('logo', { type: 'logo', src: RT_LOGO, url: RT_SITE, size: 200, align: 'center' });
const footer = block('footer', { type: 'footer', text: RT_FOOTER, align: 'center' });
export const EMAIL_STARTERS = [
    {
        key: 'enquiry-thanks',
        name: 'Thanks for your enquiry',
        description: 'Instant reply to a new lead, with a button to book a call.',
        subject: 'Thanks for reaching out, {{contact.first_name}}',
        design: {
            theme: RT_THEME,
            blocks: [
                logo,
                block('h', { type: 'heading', text: 'Thanks for reaching out, {{contact.first_name}}!', size: 26, align: 'center' }),
                block('t1', {
                    type: 'text',
                    text: 'We’ve got your details and one of our consultants will be in touch shortly.\n\nRemote Talent connects Australian businesses with skilled Virtual Assistants from the Philippines, saving you time, money and the hassle of hiring.',
                    align: 'center',
                }),
                block('b', { type: 'button', text: 'Book Your Free Consultation', url: RT_SITE, style: 'accent', align: 'center' }),
                block('t2', { type: 'text', text: 'Prefer to talk now? Call us on 1300 009 347.', align: 'center' }),
                footer,
            ],
        },
    },
    {
        key: 'book-consultation',
        name: 'Book your free consultation',
        description: 'The Staff You Need Without The Overhead: benefits and a booking button.',
        subject: 'The staff you need, without the overhead',
        design: {
            theme: RT_THEME,
            blocks: [
                logo,
                block('h', { type: 'heading', text: 'The Staff You Need Without The Overhead', size: 30, align: 'left' }),
                block('t1', { type: 'text', text: 'Hi {{contact.first_name}},\n\nHire smarter, scale faster and save more with a dedicated Virtual Assistant from Remote Talent.' }),
                block('t2', { type: 'text', text: '✔  Skilled, vetted VAs from the Philippines\n✔  Admin, bookkeeping, healthcare, sales support and more\n✔  We handle recruitment, onboarding and management' }),
                block('b', { type: 'button', text: 'Book Your Free Consultation', url: RT_SITE, style: 'accent', align: 'left' }),
                block('d', { type: 'divider' }),
                block('t3', { type: 'text', text: 'Book your free consult and see the savings add up.' }),
                footer,
            ],
        },
    },
    {
        key: 'consultation-reminder',
        name: 'Consultation reminder',
        description: 'A friendly reminder before a booked call.',
        subject: 'Reminder: your consultation with Remote Talent',
        design: {
            theme: RT_THEME,
            blocks: [
                logo,
                block('h', { type: 'heading', text: 'See you soon, {{contact.first_name}}', size: 26, align: 'center' }),
                block('t1', {
                    type: 'text',
                    text: 'This is a quick reminder about your free consultation with Remote Talent.\n\nWe’ll talk through the role you need filled and show you how much you could save.',
                    align: 'center',
                }),
                block('b', { type: 'button', text: 'Need to reschedule? Call 1300 009 347', url: 'tel:1300009347', style: 'brand', align: 'center' }),
                footer,
            ],
        },
    },
    {
        key: 'new-lead-alert',
        name: 'New lead alert (internal)',
        description: 'For the team: a new lead’s details and a link to the Sales CRM.',
        subject: 'New lead: {{contact.name}}',
        design: {
            theme: RT_THEME,
            blocks: [
                logo,
                block('h', { type: 'heading', text: 'New lead just came in', size: 24, align: 'left' }),
                block('t1', { type: 'text', text: 'Name: {{contact.name}}\nEmail: {{contact.email}}\nPhone: {{contact.phone}}' }),
                block('b', { type: 'button', text: 'Open the Sales CRM', url: 'https://app.remotetalent.com.au/sales', style: 'brand', align: 'left' }),
                block('t2', { type: 'text', text: 'Call them while they’re warm. Speed to lead wins.' }),
            ],
        },
    },
    {
        key: 'monthly-update',
        name: 'Monthly update',
        description: 'A newsletter-style update with a heading, story and call to action.',
        subject: 'What’s new at Remote Talent',
        design: {
            theme: RT_THEME,
            blocks: [
                logo,
                block('h', { type: 'heading', text: 'What’s new at Remote Talent', size: 28, align: 'center' }),
                block('t1', { type: 'text', text: 'Hi {{contact.first_name}},\n\nHere’s a quick update from the team: new roles, success stories and tips for working with your VA.' }),
                block('d', { type: 'divider' }),
                block('h2', { type: 'heading', text: 'Our most common VA roles', size: 20, align: 'left' }),
                block('t2', { type: 'text', text: 'General VA · Healthcare VA · Bookkeeper · Sales Support · Customer Service' }),
                block('b', { type: 'button', text: 'Explore our services', url: RT_SITE, style: 'brand', align: 'center' }),
                footer,
            ],
        },
    },
    {
        key: 'blank',
        name: 'Blank',
        description: 'Start from scratch with just the logo and footer.',
        subject: '',
        design: { theme: RT_THEME, blocks: [logo, block('t1', { type: 'text', text: 'Hi {{contact.first_name}},' }), footer] },
    },
];
//# sourceMappingURL=emailTemplates.js.map